import kebabCase from 'lodash/kebabCase';
import { GeoJSONStoreFeatures } from 'terra-draw';
import { v4 as uuid } from 'uuid';

import { MapPolygonsResponse, PolygonCoordinate } from '@app/models/map-polygons.model';

import { Position } from 'geojson';

/**
 * Converts GeoJSON Polygon features into { lat, lon } arrays for API requests
 *
 * @param features - array of GeoJSON features from TerraDraw
 * @returns array of polygons, each as an array of { lat, lon } points
 */
export const convertGeoJsonPolygons = (features: GeoJSONStoreFeatures[]): PolygonCoordinate[][] =>
    features
        .filter(
            feature =>
                feature.geometry?.type === 'Polygon' &&
                Array.isArray(feature.geometry.coordinates) &&
                feature.geometry.coordinates.length > 0 &&
                Array.isArray(feature.geometry.coordinates[0]),
        )
        .map(feature => {
            const coords = feature.geometry.coordinates as Position[][];

            return coords[0].map<PolygonCoordinate>(([lon, lat]) => ({ lat, lon }));
        });

/**
 * Converts polygons from API response into GeoJSON Feature objects
 *
 * @param apiPolygons - array of polygons from API in { lat, lon }[][] format
 * @returns array of GeoJSON Polygon features
 */
export const convertApiPolygonsToGeoJsonFeatures = (
    apiData: MapPolygonsResponse,
): GeoJSONStoreFeatures[] => {
    if (!apiData?.polygons) return [];

    return apiData.polygons.map(polygonsArray => ({
        id: uuid(),
        type: 'Feature',
        geometry: {
            type: 'Polygon',
            coordinates: [polygonsArray.map(({ lat, lon }) => [lon, lat])],
        },
        properties: {
            mode: 'freehand',
        },
    }));
};

/**
 * Validate a single GeoJSON polygon feature
 */
export const isValidPolygonFeature = (feature: GeoJSONStoreFeatures): boolean => {
    const { id, type, geometry, properties } = feature;

    if (
        type !== 'Feature' ||
        typeof id !== 'string' ||
        !geometry ||
        geometry.type !== 'Polygon' ||
        !Array.isArray(geometry.coordinates) ||
        geometry.coordinates.length === 0 ||
        !properties ||
        typeof properties.mode !== 'string' ||
        !['polygon', 'freehand'].includes(properties.mode)
    ) {
        return false;
    }

    const coords = geometry.coordinates[0] as Position[];
    if (!Array.isArray(coords) || coords.length < 4) return false;

    const [first, last] = [coords[0], coords[coords.length - 1]];
    if (first[0] !== last[0] || first[1] !== last[1]) return false;

    return coords.every(
        point =>
            Array.isArray(point) &&
            point.length === 2 &&
            typeof point[0] === 'number' &&
            typeof point[1] === 'number',
    );
};

/**
 * Validate an array of GeoJSON polygon features
 */
export const validateImportedPolygons = (
    data: GeoJSONStoreFeatures[],
): GeoJSONStoreFeatures[] | null => {
    if (!Array.isArray(data)) return null;

    const valid = data.filter(isValidPolygonFeature);
    return valid.length === data.length ? valid : null;
};

/**
 * Build a polygons export file name
 */
export const buildPolygonsFileName = (
    location: string,
    locationType: string,
    geoId: string,
    prefix = 'polygons',
): string => {
    const locationSlug = kebabCase(location);

    return `${prefix}_${locationSlug}_${locationType}_${geoId}.json`;
};
